<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <div class="p-4 border-b border-gray-200 flex items-center space-x-3">
      <span class="material-symbols-outlined text-xl text-primary">analytics</span>
      <h2 class="text-lg font-semibold text-gray-800">Usage Statistics</h2>
    </div>
    <div class="p-4 sm:p-6">
      <div class="space-y-6">
        <!-- Video Ideas Generated Section -->
        <div class="p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl border border-primary/20">
          <div class="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4">
            <div class="flex-shrink-0 w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
              <span class="material-symbols-outlined text-primary text-xl">smart_display</span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div class="flex-1">
                  <p class="text-sm font-medium text-gray-500 text-left">Video Ideas Generated</p>
                  <p class="mt-1 text-2xl sm:text-3xl font-bold text-gray-900 text-left">
                    {{ profile?.stats?.videoIdeasGenerated || 0 }}
                    <span v-if="generationLimit && generationLimit !== 'Unlimited'" class="text-lg font-normal text-gray-500">/ {{ generationLimit }}</span>
                  </p>
                </div>
                <div class="flex-shrink-0">
                  <span class="inline-flex items-center px-3 py-2 rounded-full text-sm font-semibold shadow-sm"
                    :class="{
                      'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-200': remainingGenerations > 2,
                      'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-200': remainingGenerations <= 2 && remainingGenerations > 0,
                      'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-200': remainingGenerations <= 0
                    }">
                    <span class="material-symbols-outlined text-sm mr-1"
                          :class="{
                            'text-green-600': remainingGenerations > 2,
                            'text-yellow-600': remainingGenerations <= 2 && remainingGenerations > 0,
                            'text-red-600': remainingGenerations <= 0
                          }">
                      {{ remainingGenerations > 2 ? 'check_circle' : remainingGenerations > 0 ? 'warning' : 'error' }}
                    </span>
                    {{ remainingGenerations }} left
                  </span>
                </div>
              </div>
              
              <!-- Progress Bar -->
              <div class="mt-6">
                <div class="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Usage Progress</span>
                  <span>{{ percentUsed }}%</span>
                </div>
                <div class="w-full h-4 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                  <div class="h-4 rounded-full transition-all duration-700 ease-out relative overflow-hidden"
                    :class="{
                      'bg-gradient-to-r from-green-400 to-green-500': percentUsed < 80,
                      'bg-gradient-to-r from-yellow-400 to-yellow-500': percentUsed >= 80 && percentUsed < 100,
                      'bg-gradient-to-r from-red-400 to-red-500': percentUsed >= 100
                    }"
                    :style="{ width: progressBarWidth }">
                    <!-- Shimmer effect -->
                    <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 animate-shimmer"></div>
                  </div>
                </div>
                <div class="flex justify-between text-xs text-gray-400 mt-2">
                  <span>0</span>
                  <span v-if="generationLimit && generationLimit !== 'Unlimited'">{{ generationLimit }}</span>
                  <span v-else class="text-primary">∞</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Current Period Info -->
        <div v-if="profile?.stats?.periodStartDate" class="flex items-start space-x-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
          <div class="flex-shrink-0 w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
            <span class="material-symbols-outlined text-blue-600 text-xl">event</span>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-500 text-left">Current Period</p>
            <p class="mt-1 text-base font-semibold text-gray-900 text-left">
              Started {{ formatDate(profile?.stats?.periodStartDate) }}
            </p>
            <div class="mt-2 inline-flex items-center px-3 py-1 bg-white rounded-full border border-blue-200 shadow-sm">
              <span class="material-symbols-outlined text-blue-600 text-xs mr-1">schedule</span>
              <span class="text-xs font-medium text-blue-800">
                Renews in {{ calculateDaysRemaining(profile?.stats?.periodStartDate) }} days
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UsageStatisticsCard',
  props: {
    profile: {
      type: Object,
      default: null
    },
    globalSettings: {
      type: Object,
      default: null
    }
  },
  computed: {
    planKey() {
      if (!this.profile?.plan?.name) return 'free';
      const normalized = this.profile.plan.name.toLowerCase();
      if (normalized.includes('free')) return 'free';
      if (normalized.includes('starter')) return 'trendztubeStarter';
      if (normalized.includes('pro')) return 'trendztubePro';
      return 'free';
    },
    planMeta() {
      return this.globalSettings?.plans?.[this.planKey] || {};
    },
    generationLimit() {
      if (!this.profile?.plan?.active) return 0;
      return this.planMeta.generationLimit ?? 0;
    },
    remainingGenerations() {
      if (!this.profile?.plan?.active) return 0;
      const used = this.profile?.stats?.videoIdeasGenerated || 0;
      const limit = this.generationLimit;
      if (limit === 0) return 'Unlimited';
      return Math.max(0, limit - used);
    },
    percentUsed() {
      const used = this.profile?.stats?.videoIdeasGenerated || 0;
      const limit = this.generationLimit;
      if (!limit || limit === 'Unlimited') return 0;
      return Math.min(100, Math.round((used / limit) * 100));
    },
    progressBarWidth() {
      if (this.generationLimit === 0 || this.generationLimit === 'Unlimited') return '0%';
      return `${this.percentUsed}%`;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    calculateDaysRemaining(periodStartDate) {
      if (!periodStartDate) return 30;
      const start = new Date(periodStartDate);
      const now = new Date();
      const daysSinceStart = Math.floor((now - start) / (1000 * 60 * 60 * 24));
      return Math.max(0, 30 - daysSinceStart);
    }
  }
};
</script>

<style scoped>
/* Enhanced gradients and animations */
.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) skewX(-12deg);
  }
  100% {
    transform: translateX(200%) skewX(-12deg);
  }
}

.animate-shimmer {
  animation: shimmer 2s infinite;
}

.shadow-inner {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .space-y-6 > * + * {
    margin-top: 1.5rem;
  }
  
  .text-2xl {
    font-size: 1.75rem;
    line-height: 2rem;
  }
  
  .sm\:text-3xl {
    font-size: 1.75rem;
    line-height: 2rem;
  }
}

/* Enhanced shadows */
.shadow-sm {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style>
